import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { TrackingApi } from 'api/TrackingApi';
import { getUserIdFromCookies } from 'helpers/userId';
import { useSystemSearchQueries } from 'hooks/useSystemSearchQueries';

const PATHNAMES_TO_EXCLUDE = ['payment', 'promote'];

const checkIsBBAvailableForPathname = (pathname: string) => {
  return !PATHNAMES_TO_EXCLUDE.find((path) => pathname.includes(path));
};

export const useRetentionBrowserBackButton = () => {
  const { pathname, search } = useLocation();

  const domain = window.location.hostname;
  const previousDomain = document.referrer;

  const {
    getBackButtonQuery,
    getReqCidQuery,
    getPartnerIdQuery,
    getPartnerLoginIdQuery,
  } = useSystemSearchQueries();

  const reqCid = getReqCidQuery();
  const partnerId = getPartnerIdQuery();
  const userId = getUserIdFromCookies();
  const partnerLoginId = getPartnerLoginIdQuery();

  const isRetentionBackButtonEnabled = useMemo(() => {
    if (getBackButtonQuery().value !== '3') return false;

    if (
      !partnerLoginId.value ||
      (+partnerLoginId.value >= 21 && +partnerLoginId.value <= 39)
    ) {
      return false;
    }

    return true;
  }, [getBackButtonQuery, partnerLoginId.value]);

  const isBBAvailableForPathname = useMemo(
    () => checkIsBBAvailableForPathname(pathname),
    [pathname]
  );

  const popStateListener = useCallback(
    (e: PopStateEvent) => {
      if (e) {
        const eventDataId = queryString.parse(search)?.event_data_id as string;

        TrackingApi.trackBackButton({
          prelandId: 99,
          screenNumber: 0,
          channel: 'backbutton-retention',
          params: {
            event_data_id: eventDataId,
          },
        })
          .then(({ data }) => {
            const params = {
              partner_id: partnerId.value,
              event_data_id: data.event_data_id,
              partner_login_id: data.partner_login_id,
              req_cid: reqCid.value,
              url: pathname,
              placement: 'ret_bb',
              user_id: userId,
            };

            window.location.replace(
              `https://parmentscommails.com/2d71c640-4d3a-4ed8-8937-81e47a62ad15?${queryString.stringify(
                params
              )}`
            );
          })
          .catch(() => {
            window.location.replace(previousDomain);
          });
      }
    },
    [partnerId.value, pathname, previousDomain, reqCid.value, userId, search]
  );

  useEffect(() => {
    if (
      isRetentionBackButtonEnabled &&
      isBBAvailableForPathname &&
      domain !== previousDomain
    ) {
      window.history.pushState({}, '', '');

      window.addEventListener('popstate', popStateListener);
    }

    return () => {
      window.removeEventListener('popstate', popStateListener);
    };
  }, [
    domain,
    isBBAvailableForPathname,
    isRetentionBackButtonEnabled,
    popStateListener,
    previousDomain,
  ]);
};
